package com.bulut.mecitogullari.ui.screens.projects

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.util.formatAmount
import com.bulut.mecitogullari.ui.components.EmptyState
import com.bulut.mecitogullari.ui.theme.AppColors
import com.bulut.mecitogullari.ui.theme.parseHexColor
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ProjectsScreen(
    onProjectClick: (projectId: String) -> Unit,
    onNewProject: () -> Unit,
    viewModel: ProjectsViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    Box(modifier = Modifier.fillMaxSize()) {
        Column(modifier = Modifier.fillMaxSize().statusBarsPadding()) {
            Text(
                text = strings.tabProjects,
                style = MaterialTheme.typography.titleLarge,
                modifier = Modifier.padding(start = 16.dp, top = 12.dp),
            )

            if (uiState.projects.isEmpty()) {
                EmptyState(strings.projectsEmpty)
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(start = 16.dp, end = 16.dp, top = 12.dp, bottom = 96.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    items(uiState.projects, key = { it.project.id }) { data ->
                        ProjectCard(data = data, onClick = { onProjectClick(data.project.id) })
                    }
                }
            }
        }

        ExtendedFloatingActionButton(
            onClick = onNewProject,
            containerColor = AppColors.Navy,
            contentColor = Color.White,
            icon = { Icon(Icons.Default.Add, contentDescription = null) },
            text = { Text(strings.newProject) },
            modifier = Modifier.align(Alignment.BottomEnd).padding(20.dp),
        )
    }
}

@Composable
private fun ProjectCard(
    data: ProjectCardData,
    onClick: () -> Unit,
) {
    val strings = LocalStrings.current
    val accent = parseHexColor(data.project.coverColorHex)
    Card(
        shape = RoundedCornerShape(18.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick),
    ) {
        Box(modifier = Modifier.fillMaxWidth().height(8.dp).background(accent))
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = data.project.name,
                        style = MaterialTheme.typography.titleMedium,
                    )
                    if (data.project.location.isNotBlank()) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.padding(top = 2.dp),
                        ) {
                            Icon(
                                Icons.Default.LocationOn,
                                contentDescription = null,
                                tint = AppColors.TextSecondary,
                                modifier = Modifier.height(16.dp),
                            )
                            Text(
                                text = data.project.location,
                                style = MaterialTheme.typography.bodySmall,
                                color = AppColors.TextSecondary,
                            )
                        }
                    }
                }
                ProgressRing(percent = data.project.progressPercent)
            }

            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                CompactStat(
                    label = strings.statSales,
                    value = if (data.totalUnits > 0) {
                        "${data.soldUnits}/${data.totalUnits}"
                    } else {
                        strings.none
                    },
                    valueColor = AppColors.TextPrimary,
                )
                CompactStat(
                    label = strings.expense,
                    value = formatAmount(data.totalExpense),
                    valueColor = AppColors.Expense,
                )
                CompactStat(
                    label = strings.income,
                    value = formatAmount(data.totalIncome),
                    valueColor = AppColors.Income
                )
                CompactStat(
                    label = strings.statNetIncome,
                    value = formatAmount(data.totalIncome - data.totalExpense),
                    valueColor = AppColors.OverAllIncome,
                )
            }
        }
    }
}

/** Small construction-progress ring with the percent in the middle. */
@Composable
private fun ProgressRing(percent: Int) {
    val color = if (percent >= 100) AppColors.Income else AppColors.Navy
    Box(contentAlignment = Alignment.Center, modifier = Modifier.size(48.dp)) {
        CircularProgressIndicator(
            progress = { percent / 100f },
            color = color,
            trackColor = AppColors.Outline,
            strokeWidth = 4.dp,
            modifier = Modifier.size(48.dp),
        )
        Text(
            text = "%$percent",
            style = MaterialTheme.typography.labelSmall,
            color = color,
            fontWeight = FontWeight.SemiBold,
        )
    }
}

@Composable
private fun CompactStat(
    label: String,
    value: String,
    valueColor: Color
) {
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = AppColors.TextSecondary,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = valueColor,
        )
    }
}
